// Thin fetch wrappers for the /ai-survey-creator backend (session-cookie auth).
import type { CreatorSessionSummary, TaskStatusResp } from './types';

const API_URL = import.meta.env.VITE_API_URL || '';
const BASE = `${API_URL}/ai-survey-creator`;

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const resp = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...init,
  });
  if (!resp.ok) {
    let detail = `Request failed (${resp.status})`;
    try {
      const body = await resp.json();
      detail = body?.detail || detail;
    } catch {
      /* non-JSON error body */
    }
    throw new Error(detail);
  }
  return resp.json() as Promise<T>;
}

export interface CreateSessionResp {
  id: number;
  title: string | null;
  created_at: string | null;
}

export interface SessionDetail {
  id: number;
  title: string | null;
  messages: { role: 'user' | 'assistant'; content: string }[];
  draft_markdown: string;
  draft_version: number;
  phase: string | null;
  brief: Record<string, unknown>;
  created_survey_id: number | null;
}

export interface TaskAccepted {
  task_id: string;
  session_id: number;
  status: string;
}

export const createSession = (title?: string) =>
  request<CreateSessionResp>('/sessions', { method: 'POST', body: JSON.stringify({ title }) });

export const listSessions = () =>
  request<{ sessions: CreatorSessionSummary[]; total: number }>('/sessions');

export const getSession = (id: number) => request<SessionDetail>(`/sessions/${id}`);

export const renameSession = (id: number, title: string) =>
  request<{ id: number; title: string | null }>(`/sessions/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ title }),
  });

export const deleteSession = (id: number) =>
  request<{ success: boolean }>(`/sessions/${id}`, { method: 'DELETE' });

export const sendMessage = (id: number, query: string, baseMarkdown?: string) =>
  request<TaskAccepted>(`/sessions/${id}/chat`, {
    method: 'POST',
    body: JSON.stringify({ query, base_markdown: baseMarkdown }),
  });

export interface EditPayload {
  instruction: string;
  selected_text?: string;
  start_line?: number | null;
  end_line?: number | null;
  occurrence?: number;
  // The client's current draft (incl. free-edits) — canonical base for the edit.
  base_markdown?: string;
}

export const applyEdit = (id: number, payload: EditPayload) =>
  request<TaskAccepted>(`/sessions/${id}/edit`, { method: 'POST', body: JSON.stringify(payload) });

export interface SavePayload {
  name?: string;
  title?: string;
  description?: string;
  instructions?: string;
  // The client's final markdown — canonical content to save (incl. free-edits).
  markdown?: string;
}

export const saveSurvey = (id: number, payload: SavePayload) =>
  request<TaskAccepted>(`/sessions/${id}/save`, { method: 'POST', body: JSON.stringify(payload) });

export const getTaskStatus = <T>(taskId: string) =>
  request<TaskStatusResp<T>>(`/status/${taskId}`);
